import type { ReactNode } from 'react';
import { cx } from './cx';
import { Button } from './Form';
import { ApiError } from '../lib/api';
import './components.css';

export interface EmptyStateProps {
  title: string;
  body?: ReactNode;
  /** One glyph. Keep it plain — this is not the place for decoration. */
  mark?: ReactNode;
  action?: { label: string; onClick: () => void };
  className?: string;
}

/** Designed, not an afterthought: say what is missing and what to do next. */
export function EmptyState({ title, body, mark = '○', action, className }: EmptyStateProps) {
  return (
    <div className={cx('k-empty', className)}>
      <div className="k-empty__mark" aria-hidden="true">{mark}</div>
      <div className="k-empty__title">{title}</div>
      {body && <div className="k-empty__body">{body}</div>}
      {action && <Button variant="ghost" onClick={action.onClick}>{action.label}</Button>}
    </div>
  );
}

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}

/**
 * Error states branch on what actually went wrong: a forbidden module is not
 * a failure to retry, and an unreachable database is not an empty night.
 */
export function ErrorState({ error, onRetry, className }: ErrorStateProps) {
  const e = ApiError.from(error);

  // A 503 is not always an outage. Most of them are "this source is not
  // connected yet", which needs a different person to do a different thing -
  // and a panel that says the database is unreachable sends whoever is reading
  // it to look at a database that is fine. The server's own reason is shown
  // when there is one; the invariant that matters is stated either way.
  const detail = e.kind === 'unavailable' ? e.detail : null;

  const title =
    e.kind === 'forbidden' ? 'You do not have access to this'
    : e.kind === 'unauthorized' ? 'Your session has ended'
    : e.kind === 'unavailable' ? (detail ? 'This cannot be answered yet' : 'The data store is unreachable')
    : e.kind === 'not_found' ? 'Nothing here'
    : 'That did not load';

  const body =
    e.kind === 'unavailable'
      ? (detail
          ? `${detail}. Nothing below is being served as zero.`
          : 'Figures are not being served rather than being served as zero. Nothing below is safe to read until this clears.')
      : e.kind === 'forbidden'
        ? 'An administrator can grant you this module. Nothing was shown because nothing was permitted.'
        : e.message;

  return (
    <div className={cx('k-empty', 'k-empty--error', className)} role="alert">
      <div className="k-empty__mark" aria-hidden="true">!</div>
      <div className="k-empty__title">{title}</div>
      <div className="k-empty__body">{body}</div>
      {onRetry && e.retryable && <Button variant="ghost" onClick={onRetry}>Try again</Button>}
    </div>
  );
}
